import { ButtonUpsellProtected } from '@components'

import { useModal } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { getBlockCreateInvoiceActionModalId } from '@features/invoices/elements/BlockCreateInvoiceActionModal/utils/getBlockCreateInvoiceActionModalId'
import { BlockCreateInvoiceActionModals } from '@features/invoices/elements/BlockCreateInvoiceActionModals'
import { BlockCreateInvoiceActionReason } from '@features/invoices/enums/blockCreateInvoiceActionReason'
import { useCanCreateFinalInvoice } from '@features/invoices/hooks/quotes/useCanCreateFinalInvoice'
import { useConvertQuoteToInvoice } from '@features/invoices/hooks/quotes/useConvertQuoteToInvoice'
import { useGuardedAction } from '@features/invoices/hooks/useGuardedAction'
import { getBlockCreateInvoiceActionReason } from '@features/invoices/utils/depositInvoices/getBlockCreateInvoiceActionReason'
import { getQuoteDepositInvoice } from '@features/invoices/utils/depositInvoices/getQuoteDepositInvoice'
import { getQuoteFinalInvoiceDraft } from '@features/invoices/utils/depositInvoices/getQuoteFinalInvoiceDraft'
import { getDiscountModeTrackingValue } from '@features/invoices/utils/getDiscountModeTrackingValue'
import { getQuoteDiscountMode } from '@features/invoices/utils/getQuoteDiscountMode'

import { Scope } from '../../../../../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../../../../../enums/trackingContext'
import { useOrganizationViewUrl } from '../../../../../../../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../../../../../../../utils/routing/reactRoute'
import { useQuote } from '../../../../../../contexts/quoteContext'

export const ConvertQuoteToFinalInvoiceButton = (): ReactElement => {
  const { t } = useTranslation()
  const { quote } = useQuote()

  const { url: finalInvoiceFromQuoteUrl } = useOrganizationViewUrl(
    reactRoute.organization.invoices.getFinalInvoiceFromQuoteRoute(String(quote?.id)),
  )

  const depositInvoiceId = getQuoteDepositInvoice(quote)?.invoiceId
  const existingFinalInvoiceDraft = getQuoteFinalInvoiceDraft(quote)
  const blockCreateInvoiceActionReason = getBlockCreateInvoiceActionReason(quote)

  const blockCreateInvoiceActionModalId = getBlockCreateInvoiceActionModalId()
  const { open: openBlockCreateInvoiceActionModal } = useModal(blockCreateInvoiceActionModalId)

  const { canCreateFinalInvoice } = useCanCreateFinalInvoice({
    depositInvoiceId,
    existingFinalInvoiceDraft,
    modalId: blockCreateInvoiceActionModalId,
  })
  const { onConvertClick: onConvertToFinalInvoiceClick } = useConvertQuoteToInvoice({
    convertToInvoiceUrl: finalInvoiceFromQuoteUrl,
    trackingDiscountMode: getDiscountModeTrackingValue(getQuoteDiscountMode(quote)),
    trackingEventName: 'XXX - Invoicing Core - Quote View - Convert to invoice button clicked',
    trackingOption: 'finalInvoice',
  })

  // An unissued deposit draft also blocks creating a final invoice - useCanCreateFinalInvoice only
  // checks an issued-but-unpaid deposit, not this earlier draft stage.
  const handleCreateFinalInvoiceButtonClick = useGuardedAction({
    isBlocked: blockCreateInvoiceActionReason === BlockCreateInvoiceActionReason.DepositInvoiceDraftExists,
    openBlockingModal: openBlockCreateInvoiceActionModal,
    proceed: async () => {
      if (await canCreateFinalInvoice()) {
        onConvertToFinalInvoiceClick()
      }
    },
  })

  return (
    <>
      <ButtonUpsellProtected
        onClick={handleCreateFinalInvoiceButtonClick}
        trackingContext={TrackingContext.QuoteConvertToInvoice}
        upsellScope={Scope.OfferConvertToDepositInvoiceUpsell}
      >
        {t('external_invoices.deposit_invoice.create_final_invoice')}
      </ButtonUpsellProtected>
      <BlockCreateInvoiceActionModals createInvoiceUrl={finalInvoiceFromQuoteUrl} quote={quote} />
    </>
  )
}
